import { observer } from 'mobx-react-lite'
import { type FC, useEffect } from 'react'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { SpinnerLoader, Typography } from 'shared/ui'
import { SettingsRoutes } from 'entities/ExportRouters'
import type { AddBankAccountStore } from '../store/addBankAccountStore'

import styles from './styles.module.scss'

type Props = { store: AddBankAccountStore }

const RETURN_URL = `${window.location.origin}/${SettingsRoutes.root}/${SettingsRoutes.planAndBilling}`

const BankAccountForm: FC<Props> = observer(({ store }) => {
  const stripe = useStripe()
  const elements = useElements()

  useEffect(() => {
    if (!stripe || !elements) return

    store.setOnConfirmSetup(async () => {
      const submitResult = await elements.submit()
      if (submitResult.error) return { error: submitResult.error }

      const { error } = await stripe.confirmSetup({
        elements,
        clientSecret: store.clientSecret!,
        confirmParams: { return_url: RETURN_URL },
        redirect: 'if_required',
      })

      return { error: error ?? null }
    })
  }, [stripe, elements])

  return (
    <div className={styles.formContent}>
      <PaymentElement
        options={
          store.billingDetails ? { defaultValues: { billingDetails: store.billingDetails } } : {}
        }
      />
      {store.error && (
        <Typography
          ariaLabel='ach_error'
          variant='body-sm-regular'
          color='var(--red-default)'
          className={styles.errorText}
        >
          {store.error}
        </Typography>
      )}
    </div>
  )
})

export const AddBankAccountContent: FC<Props> = observer(({ store }) => {
  if (!store.clientSecret) {
    return (
      <div className={styles.loaderWrapper}>
        <SpinnerLoader ariaLabel='ach_loading' />
      </div>
    )
  }

  return (
    <Elements
      stripe={store.stripePromise}
      options={{
        clientSecret: store.clientSecret,
        locale: 'en',
        appearance: {
          theme: 'stripe',
          variables: {
            colorText: '#121517',
            fontSizeBase: '13px',
          },
        },
      }}
    >
      <BankAccountForm store={store} />
    </Elements>
  )
})
